import { gql } from '@apollo/client'
import type { DocumentNode } from 'graphql'

// The board's three public reads (API contract:
// ../API/docs/frontend/menu-board-integration.md). Every query passes
// `channel: KIOSK` — the wall shows what a customer can buy in the shop, which is
// the kiosk's definition of the catalog. `channel` defaults to WEBSHOP server-side,
// so it MUST be passed.

export interface PublicVariant {
  id: number
  name: string
  price_cents: number
  deposit_cents: number
  active: boolean
  sort_order: number | null
}

export interface PublicCategoryRef {
  id: number
  name: string
}

export interface PublicProduct {
  id: number
  name: string
  description: string | null
  image_url: string | null
  sort_order: number | null
  sold_out: boolean
  attributes_json: { attributes: string[] | null; spice_level: number | null } | null
  variants: PublicVariant[] | null
  categories: PublicCategoryRef[] | null
}

export interface PublicCategory {
  id: number
  name: string
  sort_order: number | null
}

export interface PublicBundle {
  bundle_product: {
    id: number
    name: string
    description: string | null
    image_url: string | null
    variants: { price_cents: number }[] | null
  }
}

export interface CategoriesData {
  publicCategories: PublicCategory[]
}
export interface ProductsData {
  publicProducts: PublicProduct[]
}
export interface BundlesData {
  publicBundlesForVariant: PublicBundle[]
}

export interface PublicOptionItem {
  id: number
  label: string
  price_delta_cents: number
}

export interface PublicOptionGroup {
  id: number
  name: string
  min_select: number
  options: PublicOptionItem[] | null
}

/** One key per requested variant, `v<id>` (see `variantOptionGroupsQuery`). */
export type OptionGroupsData = Record<string, PublicOptionGroup[] | null>

export const GET_CATEGORIES = gql`
  query MenuCategories {
    publicCategories(channel: KIOSK) {
      id
      name
      sort_order
    }
  }
`

// One query for the whole catalog; the board filters per category client-side.
export const GET_PRODUCTS = gql`
  query MenuProducts {
    publicProducts(channel: KIOSK) {
      id
      name
      description
      image_url
      sort_order
      sold_out
      attributes_json {
        attributes
        spice_level
      }
      variants {
        id
        name
        price_cents
        deposit_cents
        active
        sort_order
      }
      categories {
        id
        name
      }
    }
  }
`

// The Meal Deal: looked up through any active curry variant, re-read with the poll.
export const GET_BUNDLES_FOR_VARIANT = gql`
  query MenuBundles($variantId: Int!) {
    publicBundlesForVariant(variant_id: $variantId, channel: KIOSK) {
      bundle_product {
        id
        name
        description
        image_url
        variants {
          price_cents
        }
      }
    }
  }
`

// The paid options of every variant on screen, in ONE request per poll: the public
// query takes a single variant_id, so the document aliases one field per variant
// (`v1: publicVariantOptionGroups(variant_id: 1, …)`). The wall only needs to know
// whether a dish can cost more than its list price (catalog.ts `isFromPrice`). An
// empty id list yields a valid document with no option field — the caller skips it,
// and `gql` never has to parse an empty selection.
export function variantOptionGroupsQuery(variantIds: number[]): DocumentNode {
  if (variantIds.length === 0) return gql('query MenuOptionGroups { __typename }')
  const fields = variantIds
    .map(
      (id) =>
        'v' +
        id +
        ': publicVariantOptionGroups(variant_id: ' +
        id +
        ', channel: KIOSK) { id name min_select options { id label price_delta_cents } }',
    )
    .join('\n')
  return gql('query MenuOptionGroups {\n' + fields + '\n}')
}
